import { Location } from '@angular/common';
import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ColDef } from '@ag-grid-community/core';
import { AccountOverdueOrder } from 'app/model/entities/-chart-classes';

import { DateFns } from 'app/shared/date-fns';
import { DbQueryService } from 'app/shared/db-query.service';
import { DomainBaseComponent } from 'app/shared/domain-base.component';
import { DomainService } from 'app/shared/domain.service';
import _ from 'lodash';
import * as ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const DEFAULT_ACCOUNT_COUNT = 20;
const WARN_DAYS = 14;
const LATE_DAYS = 20;

type SortKey = 'orderCount' | 'openValueAmt' | 'avgDaysOverdue';

/** One row of the ranked list: an account and everything it is still waiting on. */
interface AccountRow {
  accountId: number;
  accountName: string;
  orderCount: number;
  unitCount: number;
  openValueAmt: number;
  avgDaysOverdue: number;
  worstDaysOverdue: number;
}

@Component({
  selector: 'app-account-overdue-orders',
  templateUrl: './account-overdue-orders.component.html',
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class AccountOverdueOrdersComponent extends DomainBaseComponent implements OnInit, OnChanges {
  /** Accounts set to Exclude from Dashboard Analytics that this view puts back in. */
  @Input() includedAccountIds: number[] = [];
  rows: AccountRow[] = [];
  sortKey: SortKey = 'orderCount';
  accountCount = DEFAULT_ACCOUNT_COUNT;

  readonly warnDays = WARN_DAYS;
  readonly lateDays = LATE_DAYS;
  readonly sortOptions: { label: string; value: SortKey }[] = [
    { label: 'Sort: Order Count', value: 'orderCount' },
    { label: 'Sort: Open Value', value: 'openValueAmt' },
    { label: 'Sort: Average Days', value: 'avgDaysOverdue' },
  ];
  readonly accountCountOptions: { label: string; value: number }[] = [
    { label: 'Top 10', value: 10 },
    { label: 'Top 20', value: 20 },
    { label: 'Top 30', value: 30 },
    { label: 'Top 40', value: 40 },
  ];

  private detailRows: AccountOverdueOrder[] = [];
  private allRows: AccountRow[] = [];
  private barMax = 0;

  constructor(
    protected domainService: DomainService,
    protected route: ActivatedRoute,
    protected location: Location,
    protected dbQueryService: DbQueryService,
  ) {
    super(domainService);
  }

  async ngOnInit() {
    await this.load();
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (changes['includedAccountIds'] && !changes['includedAccountIds'].firstChange) {
      await this.load();
    }
  }

  private async load() {
    const data = (await this.dbQueryService.getAccountOverdueOrders(this.includedAccountIds)) ?? [];
    DateFns.fixupDatesInProjection(data, ['joDate', 'origExpectedShipDate']);

    this.detailRows = _.orderBy(data, ['accountName', 'daysOverdue'], ['asc', 'desc']);
    this.allRows = this.buildAccountRows(data);
    this.applySort();
    this.isPageReady = true;
  }

  /** One row per account. The average is weighted by the units still owed, so a 200-piece
   *  order sitting 30 days late outweighs a single-piece one — never an average of averages. */
  private buildAccountRows(data: AccountOverdueOrder[]): AccountRow[] {
    return _.map(
      _.groupBy(data, (d) => d.accountId),
      (orders) => {
        const unitCount = _.sumBy(orders, (o) => o.unshippedUnitCount);
        const weightedDays = _.sumBy(orders, (o) => o.daysOverdue * o.unshippedUnitCount);
        return {
          accountId: orders[0].accountId,
          accountName: orders[0].accountName,
          orderCount: orders.length,
          unitCount,
          openValueAmt: _.sumBy(orders, (o) => o.openValueAmt),
          avgDaysOverdue: unitCount === 0 ? 0 : _.round(weightedDays / unitCount, 1),
          worstDaysOverdue: _.max(orders.map((o) => o.daysOverdue)) ?? 0,
        } as AccountRow;
      },
    );
  }

  onSortChange() {
    this.applySort();
  }

  onAccountCountChange() {
    this.applySort();
  }

  private applySort() {
    const ranked = _.orderBy(this.allRows, (r) => r[this.sortKey], 'desc');
    this.rows = ranked.slice(0, this.accountCount);
    this.barMax = _.max(this.rows.map((r) => r[this.sortKey])) ?? 0;
  }

  /** The bar tracks whichever measure the list is ranked by, so the longest bar is always first. */
  barPct(row: AccountRow) {
    return this.barMax === 0 ? 0 : (row[this.sortKey] / this.barMax) * 100;
  }

  daysColor(row: AccountRow) {
    if (row.avgDaysOverdue >= LATE_DAYS) return 'var(--p-red-500, #ef4444)';
    if (row.avgDaysOverdue >= WARN_DAYS) return 'var(--p-yellow-500, #eab308)';
    return 'var(--p-text-color)';
  }

  isSortedBy(key: SortKey) {
    return this.sortKey === key;
  }

  getTitle() {
    return 'Overdue Units by Account';
  }

  // ViewableWidget interface methods
  async exportToExcel(): Promise<void> {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Overdue Orders');
    const exportDate = new Date();

    worksheet.addRow([this.getTitle()]);
    worksheet.getRow(1).font = { bold: true, size: 14 };
    worksheet.mergeCells('A1:G1');

    worksheet.addRow([`Exported: ${exportDate.toLocaleDateString()} ${exportDate.toLocaleTimeString()}`]);
    worksheet.getRow(2).font = { italic: true, size: 10 };
    worksheet.mergeCells('A2:G2');

    worksheet.addRow([]);

    worksheet.addRow(['Account', 'Job Order', 'Order Date', 'Promised Ship Date', 'Days Overdue', 'Units Owed', 'Open Value']);
    const headerRow = worksheet.getRow(4);
    headerRow.font = { bold: true };
    headerRow.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE0E0E0' } };

    worksheet.getColumn(1).width = 32;
    worksheet.getColumn(2).width = 12;
    worksheet.getColumn(3).width = 14;
    worksheet.getColumn(4).width = 20;
    worksheet.getColumn(5).width = 14;
    worksheet.getColumn(6).width = 12;
    worksheet.getColumn(7).width = 14;

    this.detailRows.forEach((r) => {
      worksheet.addRow([
        r.accountName,
        r.joHeaderId,
        r.joDate,
        r.origExpectedShipDate,
        r.daysOverdue,
        r.unshippedUnitCount,
        r.openValueAmt,
      ]);
    });
    worksheet.getColumn(3).numFmt = 'mm/dd/yyyy';
    worksheet.getColumn(4).numFmt = 'mm/dd/yyyy';
    worksheet.getColumn(7).numFmt = '"$"#,##0.00';

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `Overdue_Orders_By_Account_${exportDate.toISOString().split('T')[0]}.xlsx`);
  }

  getGridData(): AccountOverdueOrder[] {
    return this.detailRows;
  }

  getColumnDefs(): ColDef[] {
    return [
      { field: 'accountName', headerName: 'Account', flex: 2 },
      { field: 'joHeaderId', headerName: 'Job Order', flex: 1, type: 'leftAligned' },
      { field: 'joDate', headerName: 'Order Date', flex: 1 },
      { field: 'origExpectedShipDate', headerName: 'Promised Ship Date', flex: 1 },
      { field: 'daysOverdue', headerName: 'Days Overdue', flex: 1, type: 'leftAligned' },
      { field: 'unshippedUnitCount', headerName: 'Units Owed', flex: 1, type: 'leftAligned' },
      { field: 'openValueAmt', headerName: 'Open Value', flex: 1 },
    ];
  }

  getExportFileName(): string {
    return 'Overdue_Orders_By_Account';
  }

  getDialogTitle(): string {
    return this.getTitle();
  }
}
